import { useEffect, useRef, useState, useCallback } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function wsUrlFor(sessionId, token) {
    // REACT_APP_BACKEND_URL is https://... — swap scheme for ws.
    const base = BACKEND_URL.replace(/^http/, "ws");
    return `${base}/api/ws/sessions/${encodeURIComponent(sessionId)}?token=${encodeURIComponent(token || "")}`;
}

/**
 * useRealtimeRoom — connect to the shared-session WebSocket and stream
 * incoming messages / presence into React state.
 *
 * Args:
 *   sessionId  — the session to subscribe to (null disables the socket).
 *   token      — auth token: either the user's JWT or the session's share_token.
 *   onServerEvent — callback for `message:user` / `message:assistant` (so the
 *                   chat view can append remote turns without duplicating local
 *                   optimistic inserts).
 *   onRevoke   — fired when the server tells us sharing was revoked/rotated.
 *
 * Returns: `{ presence, typing, emitTyping }`.
 */
export function useRealtimeRoom({ sessionId, token, onServerEvent, onRevoke }) {
    const [presence, setPresence] = useState([]);
    const [typing, setTyping] = useState([]); // [{user_id, name}]
    const wsRef = useRef(null);
    const backoffRef = useRef(1000);
    const mountedRef = useRef(true);
    const typingTimeouts = useRef({});
    const onServerEventRef = useRef(onServerEvent);
    const onRevokeRef = useRef(onRevoke);

    useEffect(() => {
        onServerEventRef.current = onServerEvent;
        onRevokeRef.current = onRevoke;
    }, [onServerEvent, onRevoke]);

    useEffect(() => {
        mountedRef.current = true;
        if (!sessionId || !token) return undefined;

        let stopped = false;

        function connect() {
            if (stopped) return;
            let ws;
            try {
                ws = new WebSocket(wsUrlFor(sessionId, token));
            } catch (e) {
                console.warn("[realtime] ctor failed", e);
                return;
            }
            wsRef.current = ws;

            ws.onopen = () => {
                backoffRef.current = 1000;
            };
            ws.onmessage = (ev) => {
                let msg;
                try {
                    msg = JSON.parse(ev.data);
                } catch {
                    return;
                }
                const { event, payload } = msg || {};
                if (event === "presence:list") {
                    setPresence(payload?.members || []);
                } else if (event === "presence:join") {
                    setPresence((p) => {
                        if (p.some((m) => m.user_id === payload.user_id && payload.user_id)) return p;
                        return [...p, payload];
                    });
                } else if (event === "presence:leave") {
                    setPresence((p) => p.filter((m) => m.user_id !== payload.user_id));
                } else if (event === "typing:start") {
                    const key = payload.user_id || payload.name;
                    setTyping((t) => {
                        if (t.some((x) => (x.user_id || x.name) === key)) return t;
                        return [...t, payload];
                    });
                    if (typingTimeouts.current[key]) clearTimeout(typingTimeouts.current[key]);
                    typingTimeouts.current[key] = setTimeout(() => {
                        setTyping((t) => t.filter((x) => (x.user_id || x.name) !== key));
                        delete typingTimeouts.current[key];
                    }, 3500);
                } else if (event === "typing:stop") {
                    const key = payload.user_id || payload.name;
                    if (typingTimeouts.current[key]) {
                        clearTimeout(typingTimeouts.current[key]);
                        delete typingTimeouts.current[key];
                    }
                    setTyping((t) => t.filter((x) => (x.user_id || x.name) !== key));
                } else if (event === "message:user" || event === "message:assistant") {
                    onServerEventRef.current?.(event, payload);
                } else if (event === "session:revoked") {
                    onRevokeRef.current?.(payload?.reason || "revoked");
                }
            };
            ws.onclose = (ev) => {
                wsRef.current = null;
                if (stopped || !mountedRef.current) return;
                // 1000 = normal (owner revoked). 4xxx = auth failures — don't loop.
                if (ev.code >= 4000 && ev.code < 5000) return;
                const delay = Math.min(backoffRef.current, 15000);
                backoffRef.current = Math.min(backoffRef.current * 2, 15000);
                setTimeout(connect, delay);
            };
            ws.onerror = () => {
                /* handled by onclose */
            };
        }

        connect();

        return () => {
            stopped = true;
            mountedRef.current = false;
            Object.values(typingTimeouts.current).forEach((t) => clearTimeout(t));
            typingTimeouts.current = {};
            if (wsRef.current) {
                try {
                    wsRef.current.close(1000);
                } catch {
                    /* ignore */
                }
                wsRef.current = null;
            }
            setPresence([]);
            setTyping([]);
        };
    }, [sessionId, token]);

    const emitTyping = useCallback((start) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== 1) return;
        try {
            ws.send(JSON.stringify({ event: start ? "typing:start" : "typing:stop" }));
        } catch {
            /* ignore */
        }
    }, []);

    return { presence, typing, emitTyping };
}
